import { Send, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, InputField, Modal } from "../../components/ui";
import {
  useGtmCandidateAddMutation,
  useGtmLeadApproveMutation,
  useGtmLeadSequenceDraftMutation,
  useGtmLeadSequenceMessagesPatchMutation,
} from "../hooks/useGtmPlayLeads";

const MAX_CHARS = 8000;

const MESSAGE_DEFS = [
  { step: "first", section: "ONCE YOU'RE CONNECTED", label: "Message 1" },
  { step: "follow_up_2", section: "3 DAYS LATER, IF NO REPLY", label: "Message 2" },
  { step: "follow_up_3", section: "3 DAYS LATER, IF NO REPLY", label: "Message 3" },
] as const;

export type StartContactingPerson = {
  sourceId: string;
  kind: "candidate" | "lead";
  name: string;
  title?: string;
  company?: string;
  location?: string;
  whyFit?: string;
  linkedinUrl?: string;
  avatarUrl?: string;
  /** Present when kind is lead (already added / to approve). */
  leadId?: string;
};

type PersonProgress = "pending" | "enrolled" | "reviewed";

type PersonRuntime = {
  progress: PersonProgress;
  leadId?: string;
  bodies: Record<string, string>;
};

type Props = {
  projectId: string;
  playId: string;
  people: StartContactingPerson[];
  onClose: () => void;
  onCompleted: (summary: { ok: number; failed: number; sequencePaused: boolean }) => void;
};

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function emptyBodies(): Record<string, string> {
  return { first: "", follow_up_2: "", follow_up_3: "" };
}

function hasDraftBodies(bodies: Record<string, string>) {
  return MESSAGE_DEFS.some((d) => (bodies[d.step] || "").trim().length > 0);
}

function buildInitialRuntime(people: StartContactingPerson[]): Record<string, PersonRuntime> {
  const next: Record<string, PersonRuntime> = {};
  for (const p of people) {
    next[p.sourceId] = {
      progress: "pending",
      leadId: p.kind === "lead" ? p.leadId || p.sourceId : undefined,
      bodies: emptyBodies(),
    };
  }
  return next;
}

export default function StartContactingModal({
  projectId,
  playId,
  people,
  onClose,
  onCompleted,
}: Props) {
  const { t } = useTranslation();
  const addCandidateMutation = useGtmCandidateAddMutation(projectId, playId);
  const approveLeadMutation = useGtmLeadApproveMutation(projectId, playId);
  const draftMutation = useGtmLeadSequenceDraftMutation(projectId);
  const patchMutation = useGtmLeadSequenceMessagesPatchMutation(projectId);

  const [focusedId, setFocusedId] = useState(people[0]?.sourceId || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runtime, setRuntime] = useState<Record<string, PersonRuntime>>(() =>
    buildInitialRuntime(people)
  );

  const focused = people.find((p) => p.sourceId === focusedId) || people[0];
  const focusedRuntime = focused ? runtime[focused.sourceId] : undefined;

  const reviewedCount = useMemo(
    () => people.filter((p) => runtime[p.sourceId]?.progress === "reviewed").length,
    [people, runtime]
  );
  const notReviewedCount = people.length - reviewedCount;

  const updateRuntime = (
    sourceId: string,
    patch: Partial<PersonRuntime>,
    map: Record<string, PersonRuntime>
  ): Record<string, PersonRuntime> => {
    const next = {
      ...map,
      [sourceId]: {
        ...map[sourceId],
        ...patch,
        bodies: patch.bodies || map[sourceId]?.bodies || emptyBodies(),
      },
    };
    setRuntime(next);
    return next;
  };

  const enrollPerson = async (
    person: StartContactingPerson,
    map: Record<string, PersonRuntime>
  ): Promise<{
    leadId: string;
    map: Record<string, PersonRuntime>;
    sequencePaused: boolean;
  }> => {
    const current = map[person.sourceId];
    if (
      current?.leadId &&
      (current.progress === "enrolled" || current.progress === "reviewed")
    ) {
      return { leadId: current.leadId, map, sequencePaused: false };
    }

    let leadId = current?.leadId || person.leadId;
    if (person.kind === "candidate") {
      const lead = await addCandidateMutation.mutateAsync(person.sourceId);
      leadId = lead.id;
    }
    if (!leadId) {
      throw new Error("Missing lead id after add");
    }

    const result = await approveLeadMutation.mutateAsync({
      leadId,
      email: "",
      riskAcknowledged: true,
    });

    const next = updateRuntime(person.sourceId, { progress: "enrolled", leadId }, map);
    return { leadId, map: next, sequencePaused: result.sequencePaused };
  };

  const draftForLead = async (
    sourceId: string,
    leadId: string,
    map: Record<string, PersonRuntime>
  ): Promise<Record<string, PersonRuntime>> => {
    const seq = await draftMutation.mutateAsync(leadId);
    const bodies = emptyBodies();
    for (const step of seq.steps) {
      if (step.step in bodies) {
        bodies[step.step] = step.body || "";
      }
    }
    return updateRuntime(sourceId, { leadId, progress: "reviewed", bodies }, map);
  };

  const saveBodiesIfNeeded = async (leadId: string, bodies: Record<string, string>) => {
    if (!hasDraftBodies(bodies)) return;
    await patchMutation.mutateAsync({
      leadId,
      messages: MESSAGE_DEFS.map((d) => ({
        step: d.step,
        body: (bodies[d.step] || "").slice(0, MAX_CHARS),
      })),
    });
  };

  const onGenerate = async () => {
    if (!focused || busy) return;
    setBusy(true);
    setError(null);
    try {
      let map = runtime;
      const enrolled = await enrollPerson(focused, map);
      map = enrolled.map;
      await draftForLead(focused.sourceId, enrolled.leadId, map);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("Could not generate messages"));
    } finally {
      setBusy(false);
    }
  };

  const onContact = async () => {
    if (busy || people.length === 0) return;
    setBusy(true);
    setError(null);

    let ok = 0;
    let failed = 0;
    let sequencePaused = false;
    let firstError = "";
    let map = runtime;

    for (const person of people) {
      try {
        const enrolled = await enrollPerson(person, map);
        map = enrolled.map;
        if (enrolled.sequencePaused) sequencePaused = true;
        const bodies = map[person.sourceId]?.bodies || emptyBodies();
        if (!hasDraftBodies(bodies)) {
          map = await draftForLead(person.sourceId, enrolled.leadId, map);
        } else {
          await saveBodiesIfNeeded(enrolled.leadId, bodies);
          map = updateRuntime(
            person.sourceId,
            { leadId: enrolled.leadId, progress: "reviewed" },
            map
          );
        }
        ok += 1;
      } catch (e: any) {
        failed += 1;
        if (!firstError) {
          firstError = e?.response?.data?.message || e?.message || t("Contact failed");
        }
      }
    }

    setBusy(false);
    if (ok === 0) {
      setError(firstError || t("No people contacted"));
      return;
    }
    onCompleted({ ok, failed, sequencePaused });
  };

  const footerStatus =
    notReviewedCount === people.length
      ? `${people.length} ${t("will join, not reviewed.")}`
      : `${people.length} ${t("will join")} · ${reviewedCount} ${t("reviewed")} · ${notReviewedCount} ${t("not reviewed")}`;

  if (people.length === 0) return null;

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!busy) onClose();
      }}
      title={`${t("Start contacting")} ${people.length} ${
        people.length === 1 ? t("person") : t("people")
      }`}
      subtitle={t("Open someone to check or edit their messages.")}
      className="w-full max-w-5xl h-[min(85vh,720px)]"
      zIndex={40}
      nonClosable={busy}
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">{footerStatus}</p>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              isLoading={busy}
              onClick={onContact}
              leftIcon={<Send className="h-4 w-4" />}
            >
              {`${t("Contact")} ${people.length} ${t("on LinkedIn")}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex h-full min-h-[28rem] flex-col">
        {error ? (
          <p className="mx-5 mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-4 flex min-h-0 flex-1 border-t border-gray-200">
          <aside className="flex w-56 shrink-0 flex-col border-r border-gray-200 bg-slate-50 sm:w-64">
            <ul className="flex-1 overflow-y-auto py-1">
              {people.map((person) => {
                const active = person.sourceId === focused?.sourceId;
                const progress = runtime[person.sourceId]?.progress || "pending";
                return (
                  <li key={person.sourceId}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm ${
                        active
                          ? "bg-white font-medium text-gray-900 shadow-sm"
                          : "text-gray-700 hover:bg-white/70"
                      }`}
                      onClick={() => setFocusedId(person.sourceId)}
                      disabled={busy}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
                        {person.avatarUrl ? (
                          <img
                            src={person.avatarUrl}
                            alt=""
                            className="h-8 w-8 rounded-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          initials(person.name)
                        )}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{person.name}</span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          progress === "reviewed"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {progress === "reviewed" ? t("Reviewed") : t("Not reviewed")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <div className="min-w-0 flex-1 overflow-y-auto px-5 py-4">
            {focused ? (
              <>
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">
                    {t("Messages for")} {focused.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t("Changes here apply to this person only.")}
                  </p>
                </div>

                <div className="mb-5 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  {t("No note on the invitation.")}
                </div>

                <ol className="relative space-y-6 border-l border-gray-200 pl-6">
                  {MESSAGE_DEFS.map((def, index) => {
                    const body = focusedRuntime?.bodies[def.step] || "";
                    const drafted =
                      body.trim().length > 0 || focusedRuntime?.progress === "reviewed";
                    return (
                      <li key={def.step} className="relative">
                        <span className="absolute -left-[1.9rem] flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                          {index + 1}
                        </span>
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          {t(def.section)}
                        </p>
                        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                          <div className="mb-2 text-sm font-medium text-gray-900">
                            {t(def.label)}
                          </div>
                          {drafted ? (
                            <>
                              <InputField
                                mode="textarea"
                                rows={4}
                                value={body}
                                onChange={(v) =>
                                  setRuntime((prev) => ({
                                    ...prev,
                                    [focused.sourceId]: {
                                      ...prev[focused.sourceId],
                                      bodies: {
                                        ...prev[focused.sourceId].bodies,
                                        [def.step]: v,
                                      },
                                    },
                                  }))
                                }
                                disabled={busy}
                                placeholder={t("Write the LinkedIn message…")}
                              />
                              <div className="mt-1 text-right text-xs text-gray-400">
                                {body.length}/{MAX_CHARS}
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                              <Sparkles className="h-4 w-4 text-violet-500" />
                              {t("Will be generated by AI")}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>

                <div className="mt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    isLoading={busy}
                    onClick={onGenerate}
                    leftIcon={<Sparkles className="h-4 w-4" />}
                  >
                    {t("Generate their messages")}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
